import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CareerFocusWithMetrics } from '../../hooks/useCareerFocus';
import { useAllActiveFocuses } from '../../hooks/useAllActiveFocuses';
import { FOCUS_TYPES } from '../../constants/focusTypes';
import { 
  ChevronDownIcon, 
  BriefcaseIcon, 
  ChartBarIcon, 
  AcademicCapIcon,
  CalendarIcon,
  ArrowLeftIcon,
  HomeIcon
} from '@heroicons/react/24/outline';

interface FocusSwitcherProps {
  currentFocus?: CareerFocusWithMetrics | null;
  showBackButton?: boolean;
}

const FOCUS_ICONS = {
  internship: BriefcaseIcon,
  performance_review: ChartBarIcon,
  research: AcademicCapIcon,
  new_job: BriefcaseIcon,
  side_project: CalendarIcon,
  academic: AcademicCapIcon
};

const FOCUS_COLORS = {
  internship: 'bg-blue-500',
  performance_review: 'bg-purple-500',
  research: 'bg-green-500',
  new_job: 'bg-teal-500',
  side_project: 'bg-orange-500',
  academic: 'bg-indigo-500'
};

const FocusSwitcher: React.FC<FocusSwitcherProps> = ({ 
  currentFocus,
  showBackButton = true
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeFocuses } = useAllActiveFocuses();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd/Ctrl + K to open focus switcher
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setIsOpen(true);
      }
      // Escape to close
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleBackToHub = () => {
    // Navigate to hub - no special flag needed since we changed the redirect logic
    navigate('/focus');
  };

  const handleNavigateToFocus = (focusType: string, focusId?: string) => {
    // If focusId is provided, use the new route pattern
    if (focusId) {
      navigate(`/focus/${focusType}/${focusId}`);
    } else {
      // Fallback to old pattern for backward compatibility
      navigate(`/focus/${focusType}`);
    }
    setIsOpen(false);
  };

  const handleNavigateHome = () => {
    navigate('/home');
    setIsOpen(false);
  };

  const getCurrentFocusInfo = () => {
    if (!currentFocus) return null;
    
    const focusConfig = FOCUS_TYPES[currentFocus.focus_type] || FOCUS_TYPES.internship;
    const Icon = FOCUS_ICONS[currentFocus.focus_type] || BriefcaseIcon;
    const colorClass = FOCUS_COLORS[currentFocus.focus_type] || FOCUS_COLORS.internship;
    
    return {
      label: focusConfig.label,
      company: currentFocus.company,
      role: currentFocus.role,
      Icon,
      colorClass
    };
  };

  const isOnHub = location.pathname === '/focus';
  const focusInfo = getCurrentFocusInfo();

  return (
    <div className="flex items-center gap-2">
      {/* Back Button */}
      {showBackButton && (
        <button
          onClick={isOnHub ? handleNavigateHome : handleBackToHub}
          className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          aria-label={isOnHub ? 'Go to Home' : 'Back to Focus Hub'}
        >
          {isOnHub ? (
            <>
              <HomeIcon className="w-4 h-4" />
              <span className="text-sm font-medium">Home</span>
            </>
          ) : (
            <>
              <ArrowLeftIcon className="w-4 h-4" />
              <span className="text-sm font-medium">Focus Hub</span>
            </>
          )}
        </button>
      )}

      {/* Focus Switcher */}
      {!isOnHub && currentFocus && (
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-1.5 sm:py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
          >
            {/* Focus indicator */}
            <div className={`w-2.5 sm:w-3 h-2.5 sm:h-3 rounded-full ${focusInfo?.colorClass} flex-shrink-0`} />
            
            {/* Focus info - more compact on mobile */}
            <div className="text-left min-w-0">
              <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white truncate">
                {focusInfo?.company ? `${focusInfo.company} ${focusInfo.label}` : focusInfo?.label}
              </p>
              {focusInfo?.role && (
                <p className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 truncate">
                  {focusInfo.role}
                </p>
              )}
            </div>
            
            <ChevronDownIcon className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-gray-500 flex-shrink-0" />
          </button>

          {/* Dropdown */}
          {isOpen && (
            <div className="absolute top-full left-0 mt-1 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
              <div className="p-2">
                {/* Header */}
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Switch Focus
                </div>
                
                {/* Current Focus */}
                <button
                  onClick={() => {
                    handleNavigateToFocus(currentFocus.focus_type, currentFocus.id);
                    setIsOpen(false);
                  }}
                  className="w-full px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg mb-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${focusInfo?.colorClass}`} />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {focusInfo?.company ? `${focusInfo.company} ${focusInfo.label}` : focusInfo?.label}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Current</p>
                    </div>
                  </div>
                </button>

                {/* Other Active Focuses */}
                {activeFocuses && activeFocuses.filter(f => f.id !== currentFocus.id).map((focus) => {
                  const config = FOCUS_TYPES[focus.focus_type] || FOCUS_TYPES.internship;
                  const Icon = FOCUS_ICONS[focus.focus_type] || BriefcaseIcon;
                  const colorClass = FOCUS_COLORS[focus.focus_type] || FOCUS_COLORS.internship;
                  
                  return (
                    <button
                      key={focus.id}
                      onClick={() => handleNavigateToFocus(focus.focus_type, focus.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <div className={`w-2 h-2 rounded-full ${colorClass}`} />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {focus.company ? `${focus.company} ${config.label}` : config.label}
                        </p>
                        {focus.role && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {focus.role}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}

                {/* Separator */}
                <div className="border-t border-gray-200 dark:border-gray-700 my-2" />

                {/* Focus Hub */}
                <button
                  onClick={handleBackToHub}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <HomeIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Focus Hub</span>
                </button>

                {/* Keyboard shortcut hint */}
                <div className="px-3 py-2 mt-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                  Press <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">⌘K</kbd> to open
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FocusSwitcher;